/**
 * Where each aircraft has been told to go.
 *
 * A point command — GOTO, orbit, ROI — sends an aircraft to a coordinate the
 * operator picked, and until now that coordinate lived only in the sentence the
 * command panel printed back ("Orbiting 36.28739, 43.68250 at R120 m"). The
 * aircraft flew off and nothing on the map said where to, which is the one
 * question a commander asks about a moving track. This store is the record the
 * map draws from.
 *
 * Only ACCEPTED commands are recorded. A refused GOTO that still drew a target
 * would be a marker for a flight that is not happening, which is worse than no
 * marker at all — the same rule `uploaded` follows in missionStore.
 *
 * Two slots per aircraft, because the commands are not exclusive: `nav` is
 * where it is flying (GOTO or orbit, one at a time) and `roi` is where it is
 * looking. A new GOTO replaces a running orbit; neither touches the ROI,
 * because the aircraft really can fly one point while watching another.
 */

import { create } from 'zustand';

import { useDroneStore } from '@/stores/droneStore';
import { haversineM } from '@/stores/missionStore';
import type { DroneId } from '@/types/domain';

export type CommandTargetKind = 'goto' | 'orbit' | 'roi';

export interface CommandTarget {
  droneId: DroneId;
  kind: CommandTargetKind;
  lat: number;
  lon: number;
  /** Orbit radius in metres. Null for the kinds that have none. */
  radiusM: number | null;
  /** Commanded height in metres AGL, or null when the command carried none. */
  altM: number | null;
}

/**
 * How close counts as arrived, for a GOTO.
 *
 * An aircraft holding station over a GOTO point drifts with the wind and its
 * own position error, so a tight radius makes the marker flicker off and back
 * on as it wanders. Nothing else clears on proximity: an orbit is flown
 * continuously at its radius, and an ROI is a sensor pointing order that holds
 * until something replaces it.
 */
const ARRIVED_M = 15;

type Slot = 'nav' | 'roi';

function slotOf(kind: CommandTargetKind): Slot {
  return kind === 'roi' ? 'roi' : 'nav';
}

function keyOf(droneId: DroneId, kind: CommandTargetKind): string {
  return `${droneId}:${slotOf(kind)}`;
}

interface CommandTargetState {
  /** Keyed `droneId:slot`, so a second command in the same slot replaces the first. */
  targets: Record<string, CommandTarget>;
  reset: () => void;
}

export const useCommandTargetStore = create<CommandTargetState>((set) => ({
  targets: {},
  reset: () => set({ targets: {} }),
}));

function dropWhere(doomed: (target: CommandTarget) => boolean): void {
  const current = useCommandTargetStore.getState().targets;
  const next: Record<string, CommandTarget> = {};
  let changed = false;
  for (const [key, target] of Object.entries(current)) {
    if (doomed(target)) {
      changed = true;
      continue;
    }
    next[key] = target;
  }
  if (changed) useCommandTargetStore.setState({ targets: next });
}

/**
 * Retire a target the moment it stops being true.
 *
 * Subscribed to the fleet only while there is something to watch, and dropped
 * again when the last target clears: the drone store fires at the broadcast
 * rate for the whole fleet, and an idle console should not be paying for it.
 */
let unwatch: (() => void) | null = null;

function sweep(): void {
  const drones = useDroneStore.getState().drones;
  dropWhere((target) => {
    const drone = drones.get(target.droneId);
    // The aircraft left the fleet. A target with nothing flying to it is an
    // orphan nobody can select or cancel — the failure the mission layer's
    // roster filter already exists for, and it must not be repeated here.
    if (!drone) return true;
    if (target.kind !== 'goto') return false;
    const { lat, lon } = drone.scalars;
    if (lat === undefined || lon === undefined) return false;
    return haversineM(lat, lon, target.lat, target.lon) <= ARRIVED_M;
  });
  if (Object.keys(useCommandTargetStore.getState().targets).length === 0) {
    unwatch?.();
    unwatch = null;
  }
}

function record(target: CommandTarget): void {
  useCommandTargetStore.setState((state) => ({
    targets: { ...state.targets, [keyOf(target.droneId, target.kind)]: target },
  }));
  if (!unwatch) unwatch = useDroneStore.subscribe(sweep);
}

/**
 * Actions, at module scope and stable forever — the missionStore convention.
 * Command code calls these from a promise callback, where a hook is not
 * available and a re-render is not wanted.
 */
export const commandTargetActions = {
  recordGoto: (droneId: DroneId, lat: number, lon: number, altM: number | null) =>
    record({ droneId, kind: 'goto', lat, lon, radiusM: null, altM }),
  recordOrbit: (
    droneId: DroneId,
    lat: number,
    lon: number,
    radiusM: number,
    altM: number | null,
  ) => record({ droneId, kind: 'orbit', lat, lon, radiusM, altM }),
  recordRoi: (droneId: DroneId, lat: number, lon: number) =>
    record({ droneId, kind: 'roi', lat, lon, radiusM: null, altM: null }),
  /**
   * A command that takes the aircraft somewhere else — LAND, RTL, a mode
   * change, a mission start — retires the flight target. The marker means "it
   * is going here now", so leaving it up after the order is superseded turns it
   * into a claim about the aircraft that is no longer true.
   */
  clearNav: (droneId: DroneId) =>
    dropWhere((target) => target.droneId === droneId && slotOf(target.kind) === 'nav'),
  /** Everything for one aircraft, including where it was looking. */
  forget: (droneId: DroneId) => dropWhere((target) => target.droneId === droneId),
  list: (): CommandTarget[] => Object.values(useCommandTargetStore.getState().targets),
  reset: () => {
    unwatch?.();
    unwatch = null;
    useCommandTargetStore.getState().reset();
  },
};

/**
 * Watch the targets without React, the way the map layers do. Fires once on
 * subscribe with what is already there, so a layer mounted after a command was
 * issued still draws it.
 */
export function subscribeToCommandTargets(
  callback: (targets: CommandTarget[]) => void,
): () => void {
  let previous = useCommandTargetStore.getState().targets;
  callback(Object.values(previous));

  return useCommandTargetStore.subscribe((state) => {
    if (state.targets === previous) return;
    previous = state.targets;
    callback(Object.values(state.targets));
  });
}
